import React, { useState } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  Divider,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import WarningIcon from "@mui/icons-material/Warning";
import ScienceIcon from "@mui/icons-material/Science";
import VaccinesIcon from "@mui/icons-material/Vaccines";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import InfoIcon from "@mui/icons-material/Info";
import DownloadIcon from "@mui/icons-material/Download";
import LinkIcon from "@mui/icons-material/Link";

const TreatmentGuidelinesPage = () => {
  const [activeTab, setActiveTab] = useState(0);

  const guidelines = {
    al: {
      title: "AL (Light Chain) Amyloidosis",
      overview:
        "AL amyloidosis treatment focuses on eliminating the abnormal plasma cells producing light chains.",
      firstLine: [
        {
          name: "CyBorD (Cyclophosphamide, Bortezomib, Dexamethasone)",
          indication: "Standard first-line for most patients",
          dosing:
            "Bortezomib 1.3 mg/m² SC weekly, Cyclophosphamide 300 mg/m² PO weekly, Dexamethasone 40 mg weekly",
          duration: "6-9 cycles",
          response: "60-70% overall response rate",
          sideEffects: "Peripheral neuropathy, GI upset, fatigue",
        },
        {
          name: "Daratumumab-based regimens",
          indication:
            "Increasingly used as first-line, especially with cardiac involvement",
          dosing: "Daratumumab 16 mg/kg IV + CyBorD",
          duration: "6-24 months",
          response: "~90% overall response rate",
          sideEffects: "Infusion reactions, infections, fatigue",
        },
      ],
      transplant: {
        eligible: "Age <70, adequate organ function, ECOG 0-2",
        contraindications:
          "Severe cardiac involvement (Mayo Stage IIIb), >2 organs involved, ECOG >2",
        outcomes: "Complete response: 35-40%, 5-year survival: 77%",
      },
      maintenance: "Ixazomib or lenalidomide for 1-2 years post-transplant",
      relapsed: [
        "Daratumumab monotherapy or combinations",
        "Venetoclax (for t(11;14))",
        "Pomalidomide-based regimens",
        "Clinical trials",
      ],
      monitoring: [
        "Free light chains monthly initially",
        "NT-proBNP monthly (cardiac involvement)",
        "Organ function tests every 1-3 months",
        "Bone marrow biopsy for response assessment",
      ],
    },
    attr: {
      title: "ATTR (Transthyretin) Amyloidosis",
      overview:
        "ATTR treatment focuses on stabilizing or reducing TTR production.",
      wildtype: {
        name: "ATTRwt (Wild-type)",
        treatments: [
          {
            drug: "Tafamidis (Vyndaqel/Vyndamax)",
            dosing: "80 mg daily or 61 mg daily",
            mechanism: "TTR stabilizer",
            evidence: "ATTR-ACT trial: 30% reduction in all-cause mortality",
            monitoring:
              "Clinical assessment, echocardiogram, NT-proBNP every 3-6 months",
          },
        ],
        supportive: [
          "Diuretics for fluid management",
          "Rate control for atrial fibrillation",
          "Avoid ACE-I/ARBs (often poorly tolerated)",
          "ICD/pacemaker as indicated",
        ],
      },
      hereditary: {
        name: "ATTRv (Hereditary)",
        treatments: [
          {
            drug: "Patisiran (Onpattro)",
            dosing: "0.3 mg/kg IV every 3 weeks",
            mechanism: "RNA interference (RNAi) - reduces TTR production",
            evidence: "APOLLO trial: 74% reduction in disease progression",
            indication: "Polyneuropathy",
          },
          {
            drug: "Vutrisiran (Amvuttra)",
            dosing: "25 mg SC every 3 months",
            mechanism: "RNAi - reduces TTR production",
            evidence: "HELIOS-A trial: Non-inferior to patisiran",
            indication: "Polyneuropathy",
          },
          {
            drug: "Inotersen (Tegsedi)",
            dosing: "284 mg SC weekly",
            mechanism: "Antisense oligonucleotide - reduces TTR production",
            evidence: "NEURO-TTR trial: Significant improvement in neuropathy",
            indication: "Polyneuropathy",
          },
          {
            drug: "Tafamidis",
            dosing: "61 mg or 80 mg daily",
            mechanism: "TTR stabilizer",
            indication: "Cardiac involvement",
          },
        ],
        liverTransplant: {
          indication: "Early-stage ATTRv with specific mutations (V30M)",
          outcomes: "Stops production of mutant TTR",
          considerations:
            "Reserved for selected patients, cardiac involvement may still progress",
        },
      },
      monitoring: [
        "Echocardiogram every 3-6 months",
        "NT-proBNP and troponin every 3 months",
        "Nerve conduction studies (polyneuropathy)",
        "Quality of life assessments",
      ],
    },
    aa: {
      title: "AA (Secondary) Amyloidosis",
      overview:
        "AA amyloidosis treatment focuses on controlling the underlying inflammatory condition.",
      primaryGoal:
        "Suppress SAA protein production by treating underlying disease",
      underlyingDiseases: [
        {
          condition: "Rheumatoid Arthritis",
          treatment:
            "Disease-modifying antirheumatic drugs (DMARDs), biologics (TNF inhibitors, IL-6 inhibitors)",
        },
        {
          condition: "Inflammatory Bowel Disease",
          treatment: "Anti-TNF therapy, immunosuppressants",
        },
        {
          condition: "Chronic Infections",
          treatment: "Antibiotics, surgical drainage if needed",
        },
        {
          condition: "Familial Mediterranean Fever",
          treatment: "Colchicine, IL-1 inhibitors (anakinra, canakinumab)",
        },
      ],
      targets: [
        "SAA <10 mg/L (target)",
        "CRP normalization",
        "Control of underlying inflammatory disease",
      ],
      renal: [
        "ACE inhibitors or ARBs for proteinuria",
        "Diuretics for edema",
        "Dialysis or transplant for ESRD",
      ],
      monitoring: [
        "SAA protein levels monthly",
        "Proteinuria quantification",
        "Kidney function",
        "Assessment of underlying disease activity",
      ],
    },
    supportive: {
      title: "Supportive Care",
      cardiac: [
        "Loop diuretics (furosemide) for fluid management",
        "Beta-blockers (use cautiously, may worsen heart block)",
        "Anticoagulation for atrial fibrillation",
        "Avoid calcium channel blockers and digoxin",
        "ICD for ventricular arrhythmias",
        "Pacemaker for conduction abnormalities",
        "Consider heart transplant in selected cases",
      ],
      renal: [
        "ACE inhibitors/ARBs for proteinuria (if tolerated)",
        "Diuretics for fluid overload",
        "Dietary protein restriction",
        "Dialysis for end-stage renal disease",
        "Kidney transplant consideration",
      ],
      autonomic: [
        "Midodrine for orthostatic hypotension",
        "Fludrocortisone",
        "Compression stockings",
        "Increased salt and fluid intake",
        "Small, frequent meals for gastroparesis",
      ],
      gi: [
        "Antiemetics for nausea",
        "Loperamide or bismuth for diarrhea",
        "Pancreatic enzymes",
        "Nutritional support",
        "Prokinetics for gastroparesis",
      ],
      neuropathy: [
        "Gabapentin or pregabalin for neuropathic pain",
        "Duloxetine or amitriptyline",
        "Physical therapy",
        "Occupational therapy",
        "Assistive devices as needed",
      ],
    },
  };

  const tabContent = [
    { label: "AL Amyloidosis", content: guidelines.al },
    { label: "ATTR Amyloidosis", content: guidelines.attr },
    { label: "AA Amyloidosis", content: guidelines.aa },
    { label: "Supportive Care", content: guidelines.supportive },
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <LocalHospitalIcon sx={{ fontSize: 40, color: "primary.main" }} />
          <Typography variant="h3" fontWeight="bold">
            Treatment Guidelines
          </Typography>
          <Chip label="Free" color="success" icon={<CheckCircleIcon />} />
        </Box>
        <Typography variant="h6" color="text.secondary">
          Evidence-based treatment guidelines for amyloidosis
        </Typography>
      </Box>

      {/* Important Notice */}
      <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          <strong>For Healthcare Professionals and Educational Purposes</strong>
        </Typography>
        <Typography variant="body2">
          These guidelines are based on current evidence and expert consensus.
          Treatment should be individualized based on patient characteristics,
          comorbidities, and disease stage. Always consult with an amyloidosis
          specialist before starting treatment.
        </Typography>
      </Alert>

      {/* Main Content */}
      <Paper elevation={3}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          {tabContent.map((tab, index) => (
            <Tab key={index} label={tab.label} />
          ))}
        </Tabs>

        <Box sx={{ p: 4 }}>
          {/* AL Amyloidosis Tab */}
          {activeTab === 0 && (
            <Box>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                {guidelines.al.title}
              </Typography>
              <Typography variant="body1" paragraph>
                {guidelines.al.overview}
              </Typography>

              <Divider sx={{ my: 3 }} />

              {/* First-Line Treatment */}
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                First-Line Treatment Options
              </Typography>

              {guidelines.al.firstLine.map((regimen, index) => (
                <Card key={index} elevation={2} sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6" color="primary.main" gutterBottom>
                      {regimen.name}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>
                          <strong>Indication:</strong>
                        </Typography>
                        <Typography variant="body2" paragraph>
                          {regimen.indication}
                        </Typography>

                        <Typography variant="subtitle2" gutterBottom>
                          <strong>Dosing:</strong>
                        </Typography>
                        <Typography variant="body2" paragraph>
                          {regimen.dosing}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>
                          <strong>Duration:</strong>
                        </Typography>
                        <Typography variant="body2" paragraph>
                          {regimen.duration}
                        </Typography>

                        <Typography variant="subtitle2" gutterBottom>
                          <strong>Expected Response:</strong>
                        </Typography>
                        <Typography variant="body2" paragraph>
                          {regimen.response}
                        </Typography>

                        <Typography variant="subtitle2" gutterBottom>
                          <strong>Side Effects:</strong>
                        </Typography>
                        <Typography variant="body2">
                          {regimen.sideEffects}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))}

              <Divider sx={{ my: 3 }} />

              {/* Stem Cell Transplant */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" fontWeight="bold">
                    Autologous Stem Cell Transplantation (ASCT)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>
                        <strong>Eligibility Criteria:</strong>
                      </Typography>
                      <Typography variant="body2" paragraph>
                        {guidelines.al.transplant.eligible}
                      </Typography>

                      <Typography variant="subtitle2" gutterBottom>
                        <strong>Contraindications:</strong>
                      </Typography>
                      <Typography variant="body2">
                        {guidelines.al.transplant.contraindications}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" gutterBottom>
                        <strong>Expected Outcomes:</strong>
                      </Typography>
                      <Typography variant="body2">
                        {guidelines.al.transplant.outcomes}
                      </Typography>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Divider sx={{ my: 3 }} />

              {/* Relapsed/Refractory */}
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Relapsed/Refractory Disease
              </Typography>
              <List>
                {guidelines.al.relapsed.map((option, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <ArrowForwardIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={option} />
                  </ListItem>
                ))}
              </List>

              <Divider sx={{ my: 3 }} />

              {/* Monitoring */}
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Monitoring During Treatment
              </Typography>
              <List>
                {guidelines.al.monitoring.map((item, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <MonitorHeartIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* ATTR Amyloidosis Tab */}
          {activeTab === 1 && (
            <Box>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                {guidelines.attr.title}
              </Typography>
              <Typography variant="body1" paragraph>
                {guidelines.attr.overview}
              </Typography>

              <Divider sx={{ my: 3 }} />

              {/* ATTRwt */}
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                {guidelines.attr.wildtype.name} - Cardiac Amyloidosis
              </Typography>

              {guidelines.attr.wildtype.treatments.map((treatment, index) => (
                <Card key={index} elevation={2} sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6" color="primary.main" gutterBottom>
                      {treatment.drug}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>
                          <strong>Dosing:</strong>
                        </Typography>
                        <Typography variant="body2" paragraph>
                          {treatment.dosing}
                        </Typography>

                        <Typography variant="subtitle2" gutterBottom>
                          <strong>Mechanism:</strong>
                        </Typography>
                        <Typography variant="body2">
                          {treatment.mechanism}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" gutterBottom>
                          <strong>Evidence:</strong>
                        </Typography>
                        <Typography variant="body2" paragraph>
                          {treatment.evidence}
                        </Typography>

                        <Typography variant="subtitle2" gutterBottom>
                          <strong>Monitoring:</strong>
                        </Typography>
                        <Typography variant="body2">
                          {treatment.monitoring}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))}

              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  <strong>Supportive Care for ATTRwt:</strong>
                </Typography>
                <List dense>
                  {guidelines.attr.wildtype.supportive.map((item, index) => (
                    <ListItem key={index} sx={{ py: 0 }}>
                      <ListItemText secondary={`• ${item}`} />
                    </ListItem>
                  ))}
                </List>
              </Alert>

              <Divider sx={{ my: 3 }} />

              {/* ATTRv */}
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                {guidelines.attr.hereditary.name} - Hereditary Amyloidosis
              </Typography>

              {guidelines.attr.hereditary.treatments.map((treatment, index) => (
                <Card key={index} elevation={2} sx={{ mb: 2 }}>
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "start",
                        mb: 2,
                      }}
                    >
                      <Typography variant="h6" color="primary.main">
                        {treatment.drug}
                      </Typography>
                      <Chip
                        label={treatment.indication}
                        size="small"
                        color="primary"
                      />
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2" gutterBottom>
                          <strong>Dosing:</strong>
                        </Typography>
                        <Typography variant="body2">
                          {treatment.dosing}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2" gutterBottom>
                          <strong>Mechanism:</strong>
                        </Typography>
                        <Typography variant="body2">
                          {treatment.mechanism}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2" gutterBottom>
                          <strong>Evidence:</strong>
                        </Typography>
                        <Typography variant="body2">
                          {treatment.evidence}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))}

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" fontWeight="bold">
                    Liver Transplantation
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" gutterBottom>
                        <strong>Indication:</strong>
                      </Typography>
                      <Typography variant="body2">
                        {guidelines.attr.hereditary.liverTransplant.indication}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" gutterBottom>
                        <strong>Outcomes:</strong>
                      </Typography>
                      <Typography variant="body2">
                        {guidelines.attr.hereditary.liverTransplant.outcomes}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" gutterBottom>
                        <strong>Considerations:</strong>
                      </Typography>
                      <Typography variant="body2">
                        {
                          guidelines.attr.hereditary.liverTransplant
                            .considerations
                        }
                      </Typography>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Divider sx={{ my: 3 }} />

              {/* Monitoring */}
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Monitoring During Treatment
              </Typography>
              <List>
                {guidelines.attr.monitoring.map((item, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <MonitorHeartIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* AA Amyloidosis Tab */}
          {activeTab === 2 && (
            <Box>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                {guidelines.aa.title}
              </Typography>
              <Typography variant="body1" paragraph>
                {guidelines.aa.overview}
              </Typography>

              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  <strong>Primary Goal:</strong>
                </Typography>
                <Typography variant="body2">
                  {guidelines.aa.primaryGoal}
                </Typography>
              </Alert>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Treatment by Underlying Disease
              </Typography>

              {guidelines.aa.underlyingDiseases.map((disease, index) => (
                <Card key={index} elevation={2} sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6" color="primary.main" gutterBottom>
                      {disease.condition}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Treatment:</strong> {disease.treatment}
                    </Typography>
                  </CardContent>
                </Card>
              ))}

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Treatment Targets
              </Typography>
              <List>
                {guidelines.aa.targets.map((target, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <CheckCircleIcon color="success" />
                    </ListItemIcon>
                    <ListItemText primary={target} />
                  </ListItem>
                ))}
              </List>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Renal-Specific Management
              </Typography>
              <List>
                {guidelines.aa.renal.map((item, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <VaccinesIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Monitoring
              </Typography>
              <List>
                {guidelines.aa.monitoring.map((item, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <MonitorHeartIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Supportive Care Tab */}
          {activeTab === 3 && (
            <Box>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                {guidelines.supportive.title}
              </Typography>
              <Typography variant="body1" paragraph>
                Organ-specific supportive management for all types of
                amyloidosis
              </Typography>

              <Divider sx={{ my: 3 }} />

              {/* Cardiac */}
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <MonitorHeartIcon color="error" />
                    <Typography variant="h6" fontWeight="bold">
                      Cardiac Management
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <List>
                    {guidelines.supportive.cardiac.map((item, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <ArrowForwardIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>

              {/* Renal */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ScienceIcon color="primary" />
                    <Typography variant="h6" fontWeight="bold">
                      Renal Management
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <List>
                    {guidelines.supportive.renal.map((item, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <ArrowForwardIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>

              {/* Autonomic */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <VaccinesIcon color="warning" />
                    <Typography variant="h6" fontWeight="bold">
                      Autonomic Neuropathy
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <List>
                    {guidelines.supportive.autonomic.map((item, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <ArrowForwardIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>

              {/* GI */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <LocalHospitalIcon color="success" />
                    <Typography variant="h6" fontWeight="bold">
                      Gastrointestinal Management
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <List>
                    {guidelines.supportive.gi.map((item, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <ArrowForwardIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>

              {/* Peripheral Neuropathy */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <InfoIcon color="info" />
                    <Typography variant="h6" fontWeight="bold">
                      Peripheral Neuropathy
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <List>
                    {guidelines.supportive.neuropathy.map((item, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <ArrowForwardIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            </Box>
          )}
        </Box>
      </Paper>

      {/* References Section */}
      <Paper elevation={2} sx={{ mt: 4, p: 3 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Key References & Guidelines
        </Typography>
        <List>
          <ListItem>
            <ListItemIcon>
              <LinkIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="2024 AHA Scientific Statement on Cardiac Amyloidosis"
              secondary="Circulation. 2024"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <LinkIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="NCCN Guidelines: Systemic Light Chain Amyloidosis"
              secondary="Version 2.2024"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <LinkIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="Consensus Recommendations for ATTR Amyloidosis"
              secondary="Orphanet J Rare Dis. 2022"
            />
          </ListItem>
        </List>

        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          sx={{ mt: 2 }}
          onClick={() => alert("Download PDF feature coming soon!")}
        >
          Download Full Guidelines (PDF)
        </Button>
      </Paper>

      {/* Disclaimer */}
      <Paper elevation={1} sx={{ mt: 4, p: 3, bgcolor: "grey.50" }}>
        <Typography variant="caption" color="text.secondary">
          <strong>Disclaimer:</strong> These guidelines are for educational
          purposes and should not replace individualized medical advice.
          Treatment decisions should be made in consultation with amyloidosis
          specialists. Guidelines are updated regularly as new evidence emerges.
        </Typography>
      </Paper>
    </Container>
  );
};

export default TreatmentGuidelinesPage;
